import React, { useState } from 'react';
import { MessageCircle, X, Send } from 'lucide-react';
import { trackWhatsAppClick, buildWhatsAppUrl } from '../../utils/analytics';

const WHATSAPP_NUMBER = '917009076796'; // +91 70090 76796
const DEFAULT_MESSAGE = 'Hi! I am interested in your marketplace onboarding services.';

const WhatsAppButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState(DEFAULT_MESSAGE);

  const handleWhatsAppClick = () => {
    // Fire conversion tracking (GA4 + Google Ads) BEFORE opening WhatsApp
    trackWhatsAppClick('floating-widget');
    // buildWhatsAppUrl auto-appends utm_source + utm_medium + utm_campaign
    // derived from window.location.pathname so the conversation thread is attributable
    const whatsappUrl = buildWhatsAppUrl(WHATSAPP_NUMBER, message);
    window.open(whatsappUrl, '_blank');
    setIsOpen(false);
  };

  return (
    <>
      {/* Floating WhatsApp Button */}
      <div className="fixed bottom-24 right-6 z-50">
        {/* Chat Popup */}
        {isOpen && (
          <div 
            className="absolute bottom-16 right-0 w-80 bg-white rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-5 duration-300"
            style={{ boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-[#25D366] to-[#128C7E] p-4 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_49e3f0dc-64cb-41b7-891b-65258ccb235b/artifacts/4zp8z6bt_ZR%20landscape%20logo.png"
                      alt="Zaroori Retail"
                      className="w-10 h-10 rounded-full object-contain"
                      loading="lazy" decoding="async"
                      width="40"
                      height="40"
                    />
                  </div>
                  <div>
                    <p className="font-bold">Zaroori Retail</p>
                    <p className="text-xs text-green-100">Typically replies within minutes</p>
                  </div>
                </div>
                <button 
                  onClick={() => setIsOpen(false)}
                  className="p-1 hover:bg-white/20 rounded-full transition-colors"
                  data-testid="whatsapp-close-btn"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Chat Body */}
            <div className="p-4 bg-[#ECE5DD] min-h-[120px]">
              <div className="bg-white rounded-lg p-3 shadow-sm max-w-[85%]">
                <p className="text-sm text-gray-800">
                  👋 Hi there! How can we help you today?
                </p>
                <p className="text-xs text-gray-500 mt-1">Just now</p>
              </div>
            </div>

            {/* Input Area */}
            <div className="p-3 bg-white border-t">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Type a message..."
                  className="flex-1 px-4 py-2 bg-gray-100 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#25D366]"
                  data-testid="whatsapp-message-input"
                />
                <button
                  onClick={handleWhatsAppClick}
                  className="w-10 h-10 bg-[#25D366] rounded-full flex items-center justify-center text-white hover:bg-[#128C7E] transition-colors"
                  data-testid="whatsapp-send-btn"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
              <p className="text-xs text-gray-400 text-center mt-2">
                Powered by WhatsApp Business
              </p>
            </div>
          </div>
        )}

        {/* Main Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 ${
            isOpen 
              ? 'bg-gray-500 rotate-90' 
              : 'bg-[#25D366] hover:bg-[#128C7E] hover:scale-110'
          }`}
          style={{ boxShadow: '0 4px 20px rgba(37, 211, 102, 0.4)' }}
          data-testid="whatsapp-floating-btn"
          aria-label="Chat on WhatsApp"
        >
          {isOpen ? (
            <X className="w-6 h-6 text-white" />
          ) : (
            <svg 
              viewBox="0 0 24 24" 
              className="w-7 h-7 text-white fill-current"
            >
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
          )}
        </button>

        {/* Pulse Animation */}
        {!isOpen && (
          <span className="absolute top-0 right-0 w-4 h-4 bg-red-500 rounded-full animate-ping" />
        )}
      </div>
    </>
  );
};

export default WhatsAppButton;
